import type { MetroAnalyticsReporter } from '../interaction/metroAnalyticsReporter'

/**
 * The app prompt's events — web-only, no cross-platform contract.
 *
 * - `app_prompt_shown {surface, experiment, variant}` — the prompt is on
 *   screen. `experiment` is the Amplitude flag key, empty for the
 *   un-tested phone home prompt; `variant` the arm.
 * - `app_prompt_cta_tapped {…}` — "Get the App" / "Continue" (phone).
 * - `app_prompt_dismissed {…, action}` — "Go back to web" (`back`) or the
 *   desktop × (`close`).
 *
 * The store tap itself is the site's one conversion, `store_outbound_tapped`
 * with the arm's own slot (`StoreLinks.SLOTS.APP_PROMPT_*`), sent by the
 * dialog next to `app_prompt_cta_tapped`.
 *
 * `$exposure {flag_key, variant}` is Amplitude Experiment's exposure event:
 * it puts the visitor into the experiment's analysis. Sent only when the
 * prompt is actually on screen, and only to Amplitude — GA4 rejects names
 * starting with `$`.
 */
export type PromptSurfaceKind = 'route' | 'home' | 'desktop'

export interface PromptArm {
  readonly surface: PromptSurfaceKind
  /** Amplitude flag key; `''` for the un-tested phone home prompt. */
  readonly experiment: string
  readonly variant: string
}

const params = (arm: PromptArm) => ({
  surface: arm.surface,
  experiment: arm.experiment,
  variant: arm.variant,
})

export function reportPromptShown(reporter: MetroAnalyticsReporter, arm: PromptArm): void {
  reporter('app_prompt_shown', params(arm))
}

export function reportPromptCtaTapped(reporter: MetroAnalyticsReporter, arm: PromptArm): void {
  reporter('app_prompt_cta_tapped', params(arm))
}

export function reportPromptDismissed(
  reporter: MetroAnalyticsReporter,
  arm: PromptArm,
  action: 'back' | 'close',
): void {
  reporter('app_prompt_dismissed', { ...params(arm), action })
}

/** Amplitude Experiment exposure — straight to the Amplitude SDK. Never throws. */
export function trackExposure(arm: PromptArm): void {
  if (arm.experiment === '') return
  try {
    window.amplitude?.track?.('$exposure', { flag_key: arm.experiment, variant: arm.variant })
  } catch {
    // Analytics never breaks the page (safe-client #2).
  }
}
